import { PERSONAL_WORKSPACE_ID } from './api/workspaceContext';

/**
 * What someone may do in a workspace.
 *
 * Roles (owner / admin / member) are just named bundles of these.
 * Later we can grant extra abilities to one person, or limit them to a
 * campaign / folder, without inventing new role names.
 *
 * Pass a place as the third argument when we start scoping — ignored for now.
 */

export const CAN = {
  managePeople: 'managePeople',
  invitePeople: 'invitePeople',
  removePeople: 'removePeople',
  changeRoles: 'changeRoles',
  seeAdminOnlyDocs: 'seeAdminOnlyDocs',
  editWorkspace: 'editWorkspace',
  deleteCampaigns: 'deleteCampaigns',
  manageBilling: 'manageBilling',
};

const ALL = Object.values(CAN);

const BUNDLE = {
  owner: ALL,
  admin: [
    CAN.managePeople,
    CAN.removePeople,
    CAN.changeRoles,
    CAN.seeAdminOnlyDocs,
    CAN.editWorkspace,
    CAN.deleteCampaigns,
  ],
  user: [CAN.editWorkspace],
};

export function normalizeWorkspaceRole(raw) {
  const r = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  if (r === 'owner' || r === 'admin' || r === 'user') return r;
  if (r === 'member') return 'user';
  return 'user';
}

export function roleLabel(role) {
  const r = normalizeWorkspaceRole(role);
  if (r === 'owner') return 'Owner';
  if (r === 'admin') return 'Admin';
  return 'Member';
}

function bundleFor(role) {
  return BUNDLE[normalizeWorkspaceRole(role)] || BUNDLE.user;
}

/**
 * @param {string | { role?: string, grants?: string[] }} subject
 * @param {string} ability
 * @param {object} [_place] reserved — campaign, folder, etc.
 */
export function can(subject, ability, _place) {
  if (!ability) return false;
  const role = typeof subject === 'string' || !subject ? subject : subject.role;
  const grants = typeof subject === 'object' && subject?.grants ? subject.grants : [];
  const allowed = new Set([...bundleFor(role), ...grants]);
  return allowed.has(ability);
}

/** Docs UI tier: admin notes vs user notes. */
export function docsTierForRole(memberRole) {
  return can(memberRole, CAN.seeAdminOnlyDocs) ? 'admin' : 'user';
}

/**
 * This room is allowed: the owner is complimentary, or they have
 * an active plan covering it. Personal is covered when this account
 * has a plan. If the server has not sent `covered` yet, invited rooms
 * stay open so a lagging deploy does not lock teammates out.
 */
export function isWorkspaceCovered(ws) {
  if (!ws?.id) return false;
  if (ws.covered === true) return true;
  if (ws.covered === false) return false;
  if (ws.id === PERSONAL_WORKSPACE_ID) return false;
  return normalizeWorkspaceRole(ws.role) !== 'owner';
}

/**
 * Plan screen for this room — not for being a Fokus user.
 * No named rooms yet: Solo vs Team for the first brain (unless they already
 * have a plan, or an invite waiting). Named rooms use coverage.
 */
export function workspaceNeedsPlan(
  complimentary,
  workspaceId,
  workspaces,
  hasAccess = false,
  pendingInvites = []
) {
  if (complimentary) return false;
  // List failed or has not arrived — do not pretend they are a guest.
  if (!Array.isArray(workspaces)) return false;
  const named = workspaces.filter(
    (w) => w?.id && w.id !== PERSONAL_WORKSPACE_ID
  );
  if (named.length === 0) {
    if (hasAccess) return false;
    if ((pendingInvites || []).length > 0) return false;
    return true;
  }
  if (!workspaceId || workspaceId === PERSONAL_WORKSPACE_ID) {
    return !hasAccess;
  }
  const ws = named.find((w) => w.id === workspaceId);
  if (!ws) return true;
  return !isWorkspaceCovered(ws);
}
